import type { UserAvatarServiceAccount, UserAvatarUser } from '#layers/access/types/user-avatar';
import type {
	MonospaceAuditLog,
	ResolvedMonospaceOrgRole,
	ResolvedMonospaceWorkspace,
} from '#layers/data-model/public';
import { useI18n } from 'vue-i18n';
import { useWorkspaceDisplayNames } from '#layers/workspace/public';

export function useAccessDisplayNames() {
	const { t } = useI18n();
	const { getWorkspaceDisplayName } = useWorkspaceDisplayNames();

	function getUserDisplayName(user: UserAvatarUser): string {
		return user?.fullName ?? user?.email ?? t('common.unknown_user');
	}

	function getServiceAccountDisplayName(serviceAccount: UserAvatarServiceAccount): string {
		return serviceAccount?.fullName ?? t('common.unknown_service_account');
	}

	function getUserOrServiceAccountDisplayName(
		entity: UserAvatarUser | UserAvatarServiceAccount,
	): string {
		return 'email' in entity
			? getUserDisplayName(entity)
			: getServiceAccountDisplayName(entity);
	}

	function getAuditLogWorkspaceLabel(
		auditLog: Pick<MonospaceAuditLog, 'payload' | 'workspaceId'>,
		workspaces: ResolvedMonospaceWorkspace[] | null | undefined,
	): { label: string; isDeleted: boolean } {
		const payload = auditLog.payload as { display_name?: string; workspace_name?: string } | null;
		const payloadWorkspaceName = payload?.display_name ?? payload?.workspace_name;

		if (payloadWorkspaceName) return { label: payloadWorkspaceName, isDeleted: false };
		if (!auditLog.workspaceId) return { label: t('organization.dashboard.settings.access.audit_logs.table.system'), isDeleted: false };

		const workspace = workspaces?.find((item) => item.id === auditLog.workspaceId);
		if (!workspace) return { label: t('organization.dashboard.settings.access.audit_logs.table.deleted_workspace'), isDeleted: true };

		return { label: getWorkspaceDisplayName(workspace), isDeleted: false };
	}

	function getOrgRoleDisplayName(role: ResolvedMonospaceOrgRole | null | undefined): string {
		return role?.name ?? t('common.unknown_role');
	}

	return {
		getUserDisplayName,
		getServiceAccountDisplayName,
		getUserOrServiceAccountDisplayName,
		getAuditLogWorkspaceLabel,
		getOrgRoleDisplayName,
	};
}
